// src/components/Navbar.jsx
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme,
  Slide,
  Divider,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Chip,
  Paper
} from '@mui/material';
import {
  Email,
  Phone,
  Login,
  Menu as MenuIcon,
  ArrowDropDown,
  ArrowRight,
  Close,
  PrivacyTip,
  Diamond as DiamondIcon,
  Home,
  Info,
  Business,
  Person,
  Work,
  Help,
  ExpandLess,
  ExpandMore,
  AccountBalance,
  CreditCard,
  TrendingUp,
  SupportAgent,
  Security,
  Stars,
  Payments,
  MobileFriendly,
  Verified,
  FlashOn,
  Dashboard,
  WhatsApp,
  LocationOn
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Professional color palette - Consistent with Home page
const colors = {
  primary: '#1a1a1a',
  primaryLight: '#2d2d2d',
  primaryDark: '#0d0d0d',
  accent: '#6b21a5',
  accentLight: '#8b5cc9',
  accentDark: '#4c1d6e',
  accentGlow: 'rgba(107, 33, 165, 0.3)',
  secondary: '#e5e7eb',
  white: '#ffffff',
  black: '#000000',
  gray: '#6b7280',
  grayLight: '#9ca3af',
  grayDark: '#374151',
  gold: '#d4af37',
  lightBg: '#f8f9fa'
};

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [fastCashDialogOpen, setFastCashDialogOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  const open = Boolean(anchorEl);
  const servicesOpen = Boolean(servicesAnchorEl);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMobileServicesOpen(false);
  };
  const handleServicesMenuOpen = (event) => setServicesAnchorEl(event.currentTarget);
  const handleServicesMenuClose = () => setServicesAnchorEl(null);
  
  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    setMobileServicesOpen(false);
  };

  const handleMobileServicesToggle = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  const handleFastCashClick = () => {
    setFastCashDialogOpen(true);
  };
  const handleFastCashDialogClose = () => {
    setFastCashDialogOpen(false);
  };

  const mainNavItems = [
    { name: 'Home', path: '/', icon: <Home sx={{ fontSize: 20 }} /> },
    { name: 'About', path: '/about', icon: <Info sx={{ fontSize: 20 }} /> },
    {
      name: 'Services',
      path: '#',
      icon: <Business sx={{ fontSize: 20 }} />,
      dropdown: [
        { name: 'Personal Banking', path: '/personal', icon: <Person sx={{ fontSize: 18 }} />, description: 'Tailored solutions for individuals' },
        { name: 'Business Banking', path: '/business', icon: <Business sx={{ fontSize: 18 }} />, description: 'Grow your enterprise with us' },
        { name: 'Loans & Credit', path: '/loans', icon: <CreditCard sx={{ fontSize: 18 }} />, description: 'Flexible financing options' },
        { name: 'Investment Solutions', path: '/investments', icon: <TrendingUp sx={{ fontSize: 18 }} />, description: 'Grow your wealth securely' }
      ]
    },
    { name: 'Careers', path: '/careers', icon: <Work sx={{ fontSize: 20 }} /> },
    { name: 'Help Centre', path: '/help-centre', icon: <Help sx={{ fontSize: 20 }} /> },
    { name: 'Privacy Policy', path: '/privacy-policy', icon: <PrivacyTip sx={{ fontSize: 20 }} /> }
  ];

  const quickActions = [
    { name: 'Online Banking', icon: <Login />, href: 'https://online.diamondbank.co.sz', external: true },
    { name: 'Fast Cash', icon: <Security />, action: handleFastCashClick }
  ];

  return (
    <>
      {/* Top Contact Bar - Premium dark theme with purple accent gradient */}
      <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <Box sx={{
          background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.accentDark} 30%, ${colors.primary} 100%)`,
          color: colors.white,
          py: 0.75,
          px: 2,
          borderBottom: `1px solid ${alpha(colors.accent, 0.4)}`,
          fontFamily: 'Poppins, sans-serif',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 30% 50%, ${alpha(colors.accent, 0.2)} 0%, transparent 70%)`,
            pointerEvents: 'none'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight}, ${colors.accent})`,
            opacity: 0.6
          }
        }}>
          <Container maxWidth="xl">
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5
            }}>
              {/* Contact Info - Updated Email and Phone */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                '& .MuiButton-root': {
                  minHeight: '32px'
                }
              }}>
                
                             </Box>

              {/* Quick Actions - Online Banking and Diamond Card side by side */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                '& .MuiButton-root': {
                  minHeight: '32px'
                }
              }}>
                {/* Online Banking Button */}
                <Button
                  variant="contained"
                  startIcon={<Login />}
                  href="https://online.diamondbank.co.sz"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                    color: colors.white,
                    borderRadius: '50px',
                    px: 2.5,
                    py: 0.6,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    transition: 'all 0.3s ease',
                    textTransform: 'none',
                    letterSpacing: '0.3px',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${colors.accentLight}, ${colors.accent})`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 16px ${alpha(colors.accent, 0.5)}`
                    }
                  }}
                >
                  Online Banking
                </Button>

                {/* Diamond Card Button - Now in top bar next to Online Banking */}
                <Button
                  component="a"
                  href="/diamond-card"
                  startIcon={<DiamondIcon sx={{ color: colors.gold }} />}
                  sx={{
                    px: 2.5,
                    py: 0.6,
                    background: `linear-gradient(135deg, ${alpha(colors.accent, 0.2)}, ${alpha(colors.accentDark, 0.3)})`,
                    border: `1px solid ${alpha(colors.gold, 0.3)}`,
                    color: colors.white,
                    borderRadius: '50px',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    letterSpacing: '0.3px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    minHeight: '32px',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(90deg, transparent, ${alpha(colors.gold, 0.1)}, transparent)`,
                      transition: 'left 0.5s ease'
                    },
                    '&:hover': {
                      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                      border: `1px solid ${colors.gold}`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 16px ${alpha(colors.accent, 0.4)}`
                    },
                    '&:hover::before': {
                      left: '100%'
                    },
                    '& .MuiButton-startIcon': {
                      mr: 0.8
                    }
                  }}
                >
                  Diamond Card
                </Button>

                {/* Fast Cash Button */}
                <Button
                  variant="outlined"
                  startIcon={<Security />}
                  onClick={handleFastCashClick}
                  sx={{
                    borderColor: alpha(colors.accent, 0.4),
                    color: colors.white,
                    borderRadius: '50px',
                    px: 2.5,
                    py: 0.6,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    transition: 'all 0.3s ease',
                    textTransform: 'none',
                    letterSpacing: '0.3px',
                    minHeight: '32px',
                    '&:hover': {
                      borderColor: colors.accentLight,
                      backgroundColor: alpha(colors.accent, 0.2),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(colors.accent, 0.3)}`
                    }
                  }}
                >
                  Fast Cash
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </Slide>

      {/* Main Navbar - Premium dark theme with glass effect and purple accent */}
      <AppBar
        position="sticky"
        elevation={scrolled ? 12 : 0}
        sx={{
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          background: scrolled
            ? alpha(colors.primary, 0.92)
            : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          fontFamily: 'Poppins, sans-serif',
          py: scrolled ? 0 : 0.5,
          borderBottom: scrolled 
            ? `1px solid ${alpha(colors.accent, 0.25)}` 
            : `1px solid ${alpha(colors.accent, 0.15)}`,
          boxShadow: scrolled ? `0 4px 30px ${alpha(colors.accent, 0.15)}` : 'none',
          '&::before': scrolled ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 50% 0%, ${alpha(colors.accent, 0.1)} 0%, transparent 70%)`,
            pointerEvents: 'none'
          } : {}
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{
            py: 0,
            minHeight: '70px !important',
            transition: 'all 0.3s ease',
            px: { xs: 2, sm: 3 }
          }}>
            {/* Logo - Using top.png image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box
                component="a"
                href="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': {
                    '& .logo-glow': {
                      opacity: 1
                    }
                  }
                }}
              >
                <Box
                  component="img"
                  src="/images/top.png"
                  alt="DiamondBank Logo"
                  sx={{
                    height: scrolled ? 38 : 45,
                    width: 'auto',
                    transition: 'all 0.3s ease',
                    filter: scrolled ? 'drop-shadow(0 2px 8px rgba(107, 33, 165, 0.3))' : 'none',
                    '&:hover': { transform: 'scale(1.03)' },
                    borderRadius: '12px',
                    border: `2px solid ${alpha(colors.accent, 0.2)}`,
                    boxShadow: scrolled ? `0 0 20px ${alpha(colors.accent, 0.1)}` : 'none'
                  }}
                />
                <Box
                  className="logo-glow"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: -10,
                    right: -10,
                    bottom: -10,
                    borderRadius: '16px',
                    background: `radial-gradient(circle, ${alpha(colors.accent, 0.15)} 0%, transparent 70%)`,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    pointerEvents: 'none'
                  }}
                />
              </Box>
            </motion.div>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Navigation - Enhanced with purple accents */}
            {!isMobile && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                {mainNavItems.map((item, idx) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05, duration: 0.4 }}
                    onHoverStart={() => setHoveredItem(item.name)}
                    onHoverEnd={() => setHoveredItem(null)}
                  >
                    {item.dropdown ? (
                      <Box sx={{ position: 'relative' }}>
                        <Button
                          onClick={handleServicesMenuOpen}
                          endIcon={<ArrowDropDown sx={{
                            transition: 'transform 0.3s ease',
                            transform: servicesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            color: servicesOpen ? colors.accentLight : alpha(colors.white, 0.7)
                          }} />}
                          sx={{
                            px: 2.5,
                            py: 1.5,
                            fontWeight: 500,
                            color: colors.white,
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            position: 'relative',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: alpha(colors.accent, 0.15),
                              color: colors.accentLight
                            },
                            '&:after': {
                              content: '""',
                              position: 'absolute',
                              width: 0,
                              height: '3px',
                              bottom: '4px',
                              left: '50%',
                              background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight})`,
                              transition: 'all 0.3s ease',
                              transform: 'translateX(-50%)',
                              borderRadius: '2px'
                            },
                            '&:hover:after': {
                              width: '60%'
                            }
                          }}
                        >
                          {item.name}
                        </Button>
                        <Menu
                          anchorEl={servicesAnchorEl}
                          open={servicesOpen}
                          onClose={handleServicesMenuClose}
                          PaperProps={{
                            sx: {
                              mt: 1.5,
                              minWidth: 300,
                              borderRadius: '20px',
                              background: `linear-gradient(135deg, ${colors.white}, ${alpha(colors.accent, 0.02)} 100%)`,
                              backdropFilter: 'blur(12px)',
                              color: colors.primary,
                              boxShadow: `0 20px 60px ${alpha(colors.accent, 0.2)}`,
                              border: `1px solid ${alpha(colors.accent, 0.15)}`,
                              overflow: 'hidden',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '3px',
                                background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight}, ${colors.accent})`
                              }
                            }
                          }}
                          MenuListProps={{
                            sx: {
                              py: 1
                            }
                          }}
                        >
                          {item.dropdown.map((subItem) => (
                            <MenuItem
                              key={subItem.name}
                              onClick={handleServicesMenuClose}
                              component="a"
                              href={subItem.path}
                              sx={{
                                py: 1.5,
                                px: 2.5,
                                transition: 'all 0.3s ease',
                                borderRadius: '12px',
                                mx: 0.5,
                                '&:hover': {
                                  backgroundColor: alpha(colors.accent, 0.08),
                                  paddingLeft: '36px',
                                  '& .menu-icon-box': {
                                    backgroundColor: alpha(colors.accent, 0.2),
                                    transform: 'scale(1.1)'
                                  }
                                }
                              }}
                            >
                              <Box
                                className="menu-icon-box"
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '12px',
                                  backgroundColor: alpha(colors.accent, 0.08),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 2,
                                  color: colors.accent,
                                  transition: 'all 0.3s ease',
                                  flexShrink: 0
                                }}
                              >
                                {subItem.icon}
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary }}>
                                  {subItem.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: colors.gray, display: 'block', mt: 0.25 }}>
                                  {subItem.description}
                                </Typography>
                              </Box>
                              <ArrowRight sx={{ ml: 'auto', color: colors.gray, fontSize: 18, opacity: 0.5 }} />
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    ) : (
                      <Button
                        href={item.path}
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          fontWeight: 500,
                          color: hoveredItem === item.name ? colors.accentLight : colors.white,
                          textTransform: 'none',
                          fontSize: '0.9rem',
                          position: 'relative',
                          borderRadius: '12px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: alpha(colors.accent, 0.15),
                            color: colors.accentLight
                          },
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            width: hoveredItem === item.name ? '60%' : 0,
                            height: '3px',
                            bottom: '4px',
                            left: '50%',
                            background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight})`,
                            transition: 'all 0.3s ease',
                            transform: 'translateX(-50%)',
                            borderRadius: '2px'
                          }
                        }}
                      >
                        {item.badge ? (
                          <Badge
                            color="secondary"
                            variant="dot"
                            overlap="circular"
                            sx={{
                              mr: 1,
                              '& .MuiBadge-badge': {
                                right: 3,
                                top: 8,
                                backgroundColor: colors.accent,
                                boxShadow: `0 0 10px ${alpha(colors.accent, 0.5)}`
                              }
                            }}
                          >
                            {item.name}
                          </Badge>
                        ) : (
                          item.name
                        )}
                      </Button>
                    )}
                  </motion.div>
                ))}
              </Box>
            )}

            {/* Mobile Menu Button - Purple accent */}
            {isMobile && (
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                onClick={handleMobileDrawerToggle}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha(colors.accent, 0.2),
                    transform: 'rotate(90deg)'
                  },
                  transition: 'all 0.3s ease',
                  color: colors.white
                }}
              >
                {mobileOpen ? (
                  <Close sx={{ fontSize: '2rem', color: colors.accentLight }} />
                ) : (
                  <MenuIcon sx={{ fontSize: '2rem' }} />
                )}
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer - Premium design with purple accents */}
      <Drawer
        anchor="right"
        open={mobileOpen && isMobile}
        onClose={handleMobileDrawerToggle}
        PaperProps={{
          sx: {
            width: '88vw',
            maxWidth: 380,
            background: `linear-gradient(180deg, ${colors.white} 0%, ${alpha(colors.accent, 0.02)} 100%)`,
            borderRadius: '24px 0 0 24px',
            boxShadow: `-8px 0 40px ${alpha(colors.accent, 0.2)}`,
            overflow: 'hidden',
            borderLeft: `1px solid ${alpha(colors.accent, 0.15)}`
          }
        }}
      >
        {/* Drawer Header - Purple gradient */}
        <Box sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accentDark} 100%)`,
          borderBottom: `2px solid ${alpha(colors.accent, 0.3)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -30,
            right: -30,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(colors.accent, 0.2)} 0%, transparent 70%)`
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src="/images/top.png"
              alt="DiamondBank"
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                border: `2px solid ${alpha(colors.accent, 0.5)}`,
                boxShadow: `0 0 20px ${alpha(colors.accent, 0.2)}`
              }}
            />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700, color: colors.white }}>
                DiamondBank
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(colors.white, 0.7) }}>
                Premium Banking
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleMobileDrawerToggle} sx={{ color: colors.white }}>
            <Close />
          </IconButton>
        </Box>

        {/* Diamond Card Link in Mobile */}
        <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${alpha(colors.accent, 0.1)}` }}>
          <Button
            fullWidth
            component="a"
            href="/diamond-card"
            startIcon={<DiamondIcon sx={{ color: colors.gold }} />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(colors.accent, 0.15)}, ${alpha(colors.accentDark, 0.2)})`,
              border: `1px solid ${alpha(colors.gold, 0.3)}`,
              color: colors.primary,
              borderRadius: '50px',
              py: 1.2,
              fontWeight: 700,
              fontSize: '0.9rem',
              textTransform: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                color: colors.white,
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 16px ${alpha(colors.accent, 0.3)}`
              }
            }}
          >
            Diamond Card
          </Button>
        </Box>

        {/* Quick Actions in Drawer */}
        <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${alpha(colors.gray, 0.08)}` }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Login />}
            href="https://online.diamondbank.co.sz"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
              color: colors.white,
              borderRadius: '50px',
              py: 1.2,
              fontWeight: 700,
              fontSize: '0.9rem',
              textTransform: 'none',
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.accentLight}, ${colors.accent})`,
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 16px ${alpha(colors.accent, 0.4)}`
              },
              transition: 'all 0.3s ease'
            }}
          >
            Online Banking
          </Button>
        </Box>

        {/* Navigation List */}
        <List sx={{ px: 1.5, py: 1 }}>
          {mainNavItems.map((item) => (
            <React.Fragment key={item.name}>
              {item.dropdown ? (
                <>
                  <ListItem
                    component="div"
                    onClick={handleMobileServicesToggle}
                    sx={{
                      borderRadius: '14px',
                      mb: 0.5,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: alpha(colors.accent, 0.08)
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: colors.accent }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        color: colors.primary,
                        fontSize: '0.95rem'
                      }}
                    />
                    {mobileServicesOpen ? (
                      <ExpandLess sx={{ color: colors.accent }} />
                    ) : (
                      <ExpandMore sx={{ color: colors.gray }} />
                    )}
                  </ListItem>
                  <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.dropdown.map((subItem) => (
                        <ListItem
                          key={subItem.name}
                          component="a"
                          href={subItem.path}
                          onClick={handleMobileDrawerToggle}
                          sx={{
                            pl: 6,
                            py: 1.2,
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: alpha(colors.accent, 0.06)
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: colors.accent }}>
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={subItem.name}
                            secondary={subItem.description}
                            primaryTypographyProps={{
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              color: colors.primary
                            }}
                            secondaryTypographyProps={{
                              fontSize: '0.7rem',
                              color: colors.gray
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem
                  component="a"
                  href={item.path}
                  onClick={handleMobileDrawerToggle}
                  sx={{
                    borderRadius: '14px',
                    mb: 0.5,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: alpha(colors.accent, 0.08),
                      paddingLeft: '28px'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: colors.accent }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: colors.primary,
                      fontSize: '0.95rem'
                    }}
                  />
                  {item.badge && (
                    <Badge
                      variant="dot"
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: colors.accent,
                          boxShadow: `0 0 8px ${alpha(colors.accent, 0.5)}`
                        }
                      }}
                    />
                  )}
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>

        {/* Drawer Footer */}
        <Box sx={{
          mt: 'auto',
          p: 2.5,
          borderTop: `1px solid ${alpha(colors.accent, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(colors.accent, 0.03)}, ${alpha(colors.accentDark, 0.05)})`
        }}>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Security sx={{ color: colors.accent }} />}
              onClick={handleFastCashClick}
              sx={{
                borderRadius: '50px',
                borderColor: colors.accent,
                color: colors.accent,
                fontSize: '0.75rem',
                px: 2.5,
                '&:hover': {
                  borderColor: colors.accentLight,
                  backgroundColor: alpha(colors.accent, 0.08),
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Fast Cash
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<WhatsApp sx={{ color: '#25D366' }} />}
              href="https://wa.me/27763814178"
              target="_blank"
              sx={{
                borderRadius: '50px',
                borderColor: '#25D366',
                color: '#25D366',
                fontSize: '0.75rem',
                px: 2.5,
                '&:hover': {
                  backgroundColor: alpha('#25D366', 0.08),
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              WhatsApp
            </Button>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: colors.gray }}>
              Need help? Call us: <span style={{ color: colors.accent, fontWeight: 600 }}>+27 76 381 4178</span>
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Fast Cash Coming Soon Dialog - Premium design with purple */}
      <Dialog
        open={fastCashDialogOpen}
        onClose={handleFastCashDialogClose}
        aria-labelledby="fast-cash-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: '28px',
            overflow: 'hidden',
            background: colors.white,
            boxShadow: `0 32px 64px ${alpha(colors.accent, 0.25)}`,
            maxWidth: '420px',
            width: '92%',
            border: `1px solid ${alpha(colors.accent, 0.1)}`
          }
        }}
      >
        <Box sx={{
          background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
          textAlign: 'center',
          py: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: alpha(colors.white, 0.05)
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: alpha(colors.white, 0.03)
          }
        }}>
          <DiamondIcon sx={{ fontSize: 72, color: colors.white, mb: 1.5, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }} />
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.white, letterSpacing: '-0.5px' }}>
            Coming Soon!
          </Typography>
        </Box>
        <DialogContent sx={{ py: 3.5, px: 3.5, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: colors.accent }}>
            Fast Cash Service
          </Typography>
          <Typography variant="body2" sx={{ color: colors.gray, lineHeight: 1.7 }}>
            Our Fast Cash service is currently under development and will be available soon.
            Get ready for instant access to funds when you need them most.
          </Typography>
          <Box sx={{
            mt: 3,
            p: 2.5,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${alpha(colors.accent, 0.05)}, ${alpha(colors.accentDark, 0.05)})`,
            border: `1px solid ${alpha(colors.accent, 0.1)}`
          }}>
            <Typography variant="body2" sx={{ color: colors.grayDark }}>
              <span style={{ fontSize: '1.2rem' }}>💎</span> Stay tuned for updates from DiamondBank!
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3.5, px: 3.5 }}>
          <Button
            onClick={handleFastCashDialogClose}
            variant="contained"
            fullWidth
            sx={{
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
              color: colors.white,
              borderRadius: '50px',
              py: 1.2,
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'none',
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.accentLight}, ${colors.accent})`,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px ${alpha(colors.accent, 0.4)}`
              },
              transition: 'all 0.3s ease'
            }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;